import { Currency, CurrencyAmount, Token, V2_ROUTER_ADDRESSES } from '@kodiak-finance/sdk-core'
import FiatValue from 'components/FiatValue'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import PriceImpactWarning from 'components/swap/PriceImpactWarning'
import { ErrorText } from 'components/swap/styleds'
import { Switch } from 'components/Switch'
import { BAULT_ROUTER_ADDRESSES } from 'constants/addresses'
import { MAX_ZAP_SLIPPAGE_PERCENTAGE, MIN_ZAP_SLIPPAGE_PERCENTAGE } from 'constants/zap'
import { useToken } from 'hooks/Tokens'
import { useBaultBasicInfo } from 'hooks/useBault'
import { useConnection } from 'hooks/useConnection'
import { useInputToken } from 'hooks/useInputToken'
import { useIsland } from 'hooks/useIsland'
import { useKodiakFarmByPool } from 'hooks/useKodiakFarm'
import { useSmartIslandRouter } from 'hooks/useSmartIslandRouter'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { useSendTransaction, useWriteContract } from 'hooks/useTransaction'
import { useUSDValue } from 'hooks/useUSDValue'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { RefreshButton } from 'pages/Liquidity/Liquidity.styles'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw } from 'react-feather'
import { Field as CharmField } from 'state/charm/mint/actions'
import { useDerivedMintCharmInfo, useMintCharmActionHandlers, useMintCharmState } from 'state/charm/mint/hooks'
import {
  useDerivedMintInfo as useV2DerivedMintInfo,
  useMintActionHandlers as useV2MintActionHandlers,
  useMintState as useV2MintState,
} from 'state/mint/hooks'
import { warningSeverity } from 'utils/prices'
import { Address, Hash } from 'viem'

import { ButtonError, ButtonLight, ButtonPrimary } from '../../../../components/Button'
import { AutoColumn } from '../../../../components/Column'
import CurrencyInputPanel from '../../../../components/CurrencyInputPanel'
import { useClearKxQuoteCache } from '../../../../hooks/backend/useClearKxQuoteCache'
import { useIsSwapUnsupported } from '../../../../hooks/useIsSwapUnsupported'
import useTransactionDeadline from '../../../../hooks/useTransactionDeadline'
import { formatSwapAmount } from '../../../../lib/utils/formatAmount'
import { useToggleWalletModal } from '../../../../state/application/hooks'
import {
  useDerivedMintInfo as useIslandDerivedMintInfo,
  useMintActionHandlers as useIslandMintActionHandlers,
  useMintState as useIslandMintState,
  useMintStateSingleSidedCurrency,
  useSingleSidedKXRouterMintInfo,
} from '../../../../state/islands/mint/hooks'
import { Field } from '../../../../state/mint/actions'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { TransactionType } from '../../../../state/transactions/types'
import { ThemedText } from '../../../../theme'
import { maxAmountSpend } from '../../../../utils/maxAmountSpend'
import { IslandContextType } from '../context/IslandContextType'
import AutoCompoundDets from './components/AutoCompoundDets'
import { useOnBaultDeposit, useOnStake, useOnVaultStake } from './hooks/useOnStakeCallback'
import { usePreviewBaultShares } from './hooks/usePreviewBaultShares'
import { useVaultStrategy } from './hooks/useVaultStrategy'
import BaultDepositStep from './steps/BaultDepositStep'
import { useIslandDepositStep } from './steps/IslandDepositStep'
import RewardVaultStakeStep from './steps/RewardVaultStakeStep'
import { useSelectLockTimeStep } from './steps/SelectLockTimeStep'
import StakeStep from './steps/StakeStep'
import { DoubleSidedParams, NativeParams, SingleSidedParams } from './strategies/vault/VaultStrategy.type'
import * as S from './styleds'

const DepositView: React.FC<IslandContextType> = ({
  currencyA,
  currencyB,
  // wrappedNativeCurrency,
  feeAmount,
  poolType,
  islandId: poolAddress,
  rewardVault,
  rewardVaultRewardToken,
  routerAddress,
  stakingData: { stakingType, kodiakFarmAddress, baultAddress, baultFarmAddress },
  // config,
  poolApiResponse,
  isRewardVaultStakingDisabled,
}) => {
  const kodiakFarm = useKodiakFarmByPool(kodiakFarmAddress)
  const toggleWalletModal = useToggleWalletModal()
  const [autoCompoundSelected, setAutoCompoundSelected] = useState(!!baultAddress)
  const useAutoCompound = autoCompoundSelected && !isRewardVaultStakingDisabled
  const [simulationError, setSimulationError] = useState<string>('')
  const [isRefreshingZapQuote, setIsRefreshingZapQuote] = useState(false)
  const { account, chainId, wagmiPublicClient, wagmiWalletClient } = useConnection()
  const { writeContractAsync } = useWriteContract()
  const { sendTransactionAsync } = useSendTransaction()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const clearQuoteCache = useClearKxQuoteCache()
  const islandZapRouterAddress = BAULT_ROUTER_ADDRESSES[chainId]
  const v2RouterAddress = V2_ROUTER_ADDRESSES[chainId] as Address

  const nativeCurrency = useNativeCurrency()
  const wberaToken = nativeCurrency.wrapped

  const [doubleSidedCurrencies, setDoubleSidedCurrencies] = useState<{ [field in Field]: Currency }>({
    [Field.CURRENCY_A]: currencyA,
    [Field.CURRENCY_B]: currencyB,
  })

  const selectedCurrencyA = doubleSidedCurrencies[Field.CURRENCY_A]
  const selectedCurrencyB = doubleSidedCurrencies[Field.CURRENCY_B]

  const [depositOption, setDepositOption] = useState<'depositOnly' | 'withStaking'>(
    baultAddress ? 'depositOnly' : 'withStaking'
  )

  const userInteractedRef = useRef(false)

  const { data } = useBaultBasicInfo(baultAddress)

  const kodiakFarmForBaultExists = Boolean(baultFarmAddress && baultAddress)
  const shouldDisableWithStaking = Boolean(baultAddress && !baultFarmAddress)
  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol
  const rewardVaultLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} Reward Vault` : 'Reward Vault'

  // Get mint state based on pool type - called at top level
  const v2MintState = useV2MintState()
  const islandMintState = useIslandMintState()
  const charmMintState = useMintCharmState()
  const singleSidedCurrency = useMintStateSingleSidedCurrency()

  // When single-sided mode is active or reward vault staking is disabled, ensure depositOption is set to 'depositOnly'
  useEffect(() => {
    if ((islandMintState.singleSided || isRewardVaultStakingDisabled) && depositOption === 'withStaking') {
      setDepositOption('depositOnly')
    }
  }, [islandMintState.singleSided, isRewardVaultStakingDisabled, depositOption])

  // Get the appropriate vault strategy based on pool type
  const vaultType = useMemo(() => {
    switch (poolType) {
      case 'Island':
        return 'island'
      case 'Charm':
        return 'charmvault'
      case 'V2':
        return 'v2'
      default:
        return 'island'
    }
  }, [poolType])

  // Get aligned tokens for Island pools
  const { alignedTokens } = useMemo(() => {
    if (islandMintState.singleSided) {
      return {
        alignedTokens: singleSidedCurrency?.isNative ? [] : [singleSidedCurrency],
      }
    }

    const tokens: Currency[] = []
    if (selectedCurrencyA && !selectedCurrencyA.isNative) tokens.push(selectedCurrencyA)
    if (selectedCurrencyB && !selectedCurrencyB.isNative) tokens.push(selectedCurrencyB)

    return { alignedTokens: tokens }
  }, [islandMintState.singleSided, singleSidedCurrency, selectedCurrencyA, selectedCurrencyB])

  const smartIslandRouter = useSmartIslandRouter({
    mode: islandMintState.singleSided
      ? singleSidedCurrency?.isNative
        ? 'singleNative'
        : 'singleToken'
      : 'doubleSided',
    tokens: alignedTokens,
    needsBaultFeatures: useAutoCompound && !!baultAddress,
  })

  // Setup vault strategy with dependencies
  const dependencies = useMemo(() => {
    switch (vaultType) {
      case 'island':
        return {
          smartIslandRouterAddress: smartIslandRouter?.address,
          wagmiPublicClient,
          writeContractAsync,
          chainId,
          sendTransactionAsync,
        }
      case 'charmvault':
        return {
          charmVaultAddress: poolAddress,
          wagmiPublicClient,
          writeContractAsync,
          chainId,
          sendTransactionAsync,
        }
      case 'v2':
        return {
          v2RouterAddress,
          wagmiPublicClient,
          writeContractAsync,
          chainId,
        }
      default:
        return null
    }
  }, [
    poolAddress,
    vaultType,
    smartIslandRouter?.address,
    wagmiPublicClient,
    writeContractAsync,
    chainId,
    v2RouterAddress,
    sendTransactionAsync,
  ])

  const { strategy, config: vaultConfig } = useVaultStrategy(vaultType, dependencies, islandMintState.singleSided)

  const { independentField, typedValue, otherTypedValue } = useMemo(() => {
    switch (poolType) {
      case 'V2':
        return v2MintState
      case 'Charm':
        return charmMintState
      default:
        return islandMintState
    }
  }, [poolType, v2MintState, islandMintState, charmMintState])

  const effectiveCurrencies = useMemo(() => {
    if (poolType === 'Charm') {
      return {
        [Field.CURRENCY_A]: selectedCurrencyA?.isNative ? selectedCurrencyA.wrapped : selectedCurrencyA,
        [Field.CURRENCY_B]: selectedCurrencyB?.isNative ? selectedCurrencyB.wrapped : selectedCurrencyB,
      }
    }
    return {
      [Field.CURRENCY_A]: selectedCurrencyA,
      [Field.CURRENCY_B]: selectedCurrencyB,
    }
  }, [poolType, selectedCurrencyA, selectedCurrencyB])

  // Get mint info based on pool type
  const v2MintInfo = useV2DerivedMintInfo(effectiveCurrencies.CURRENCY_A, effectiveCurrencies.CURRENCY_B)
  const islandMintInfo = useIslandDerivedMintInfo(
    poolType === 'Island' ? poolAddress : undefined,
    effectiveCurrencies.CURRENCY_A,
    effectiveCurrencies.CURRENCY_B,
    effectiveCurrencies.CURRENCY_A
  )

  // Get the island data from the hook
  const { island: islandData } = useIsland(poolType === 'Island' ? poolAddress : undefined)

  const kxRouterMintInfo = useSingleSidedKXRouterMintInfo(
    islandMintState.singleSided ? islandData : null,
    useAutoCompound,
    baultAddress,
    poolType,
    poolAddress
  )

  const charmMintInfo = useDerivedMintCharmInfo(
    poolType === 'Charm' ? poolAddress : undefined,
    effectiveCurrencies.CURRENCY_A,
    effectiveCurrencies.CURRENCY_B
  )

  const {
    dependentField,
    currencies,
    pool,
    poolState,
    currencyBalances,
    parsedAmounts,
    price,
    liquidityMinted,
    error,
    amount0Max,
    amount1Max,
    amount0Min,
    amount1Min,
    amountSharesMin,
    amountShares,
    singleSided: poolSingleSided,
    dependentAmountFetching,
  } = useMemo(() => {
    switch (poolType) {
      case 'V2': {
        const {
          dependentField,
          currencies,
          pair: pool,
          pairState: poolState,
          currencyBalances,
          parsedAmounts,
          price,
          liquidityMinted,
          error,
        } = v2MintInfo

        return {
          dependentField,
          currencies,
          pool,
          poolState,
          currencyBalances,
          parsedAmounts,
          price,
          liquidityMinted,
          error,
          amount0Max: parsedAmounts[Field.CURRENCY_A],
          amount1Max: parsedAmounts[Field.CURRENCY_B],
          amount0Min: parsedAmounts[Field.CURRENCY_A]?.multiply(98).divide(100),
          amount1Min: parsedAmounts[Field.CURRENCY_B]?.multiply(98).divide(100),
          amountSharesMin: liquidityMinted?.multiply(99).divide(100),
          amountShares: liquidityMinted,
          singleSided: false,
          dependentAmountFetching: false,
        }
      }
      case 'Charm': {
        const mappedField = independentField === CharmField.CURRENCY_A ? Field.CURRENCY_A : Field.CURRENCY_B
        const mappedDependentField =
          charmMintInfo.dependentField === CharmField.CURRENCY_A ? Field.CURRENCY_A : Field.CURRENCY_B

        return {
          dependentField: mappedDependentField,
          currencies: {
            [Field.CURRENCY_A]: charmMintInfo.currencies[CharmField.CURRENCY_A],
            [Field.CURRENCY_B]: charmMintInfo.currencies[CharmField.CURRENCY_B],
          },
          pool: null,
          poolState: 0,
          currencyBalances: {
            [Field.CURRENCY_A]: charmMintInfo.currencyBalances[CharmField.CURRENCY_A],
            [Field.CURRENCY_B]: charmMintInfo.currencyBalances[CharmField.CURRENCY_B],
          },
          parsedAmounts: {
            [Field.CURRENCY_A]: charmMintInfo.parsedAmounts[CharmField.CURRENCY_A],
            [Field.CURRENCY_B]: charmMintInfo.parsedAmounts[CharmField.CURRENCY_B],
          },
          price: undefined,
          liquidityMinted: charmMintInfo.expectedShares,
          error: charmMintInfo.error,
          amount0Max: charmMintInfo.parsedAmounts[CharmField.CURRENCY_A],
          amount1Max: charmMintInfo.parsedAmounts[CharmField.CURRENCY_B],
          amount0Min: charmMintInfo.amount0Min,
          amount1Min: charmMintInfo.amount1Min,
          amountSharesMin: charmMintInfo.expectedSharesMin,
          amountShares: charmMintInfo.expectedShares,
          singleSided: false,
          dependentAmountFetching: charmMintInfo.dependentAmountFetching,
          // Pass through Charm-specific data
          vaultTotalSupply: charmMintInfo.vaultTotalSupply,
          vaultAmount0: charmMintInfo.vaultAmount0,
          vaultAmount1: charmMintInfo.vaultAmount1,
        }
      }
      default:
        return islandMintInfo
    }
  }, [
    poolType,
    islandMintInfo,
    v2MintInfo,
    independentField,
    charmMintInfo.dependentField,
    charmMintInfo.currencies,
    charmMintInfo.currencyBalances,
    charmMintInfo.parsedAmounts,
    charmMintInfo.expectedShares,
    charmMintInfo.error,
    charmMintInfo.amount0Min,
    charmMintInfo.amount1Min,
    charmMintInfo.expectedSharesMin,
    charmMintInfo.dependentAmountFetching,
    charmMintInfo.vaultTotalSupply,
    charmMintInfo.vaultAmount0,
    charmMintInfo.vaultAmount1,
  ])

  // Get action handlers based on pool type
  const islandHandler = useIslandMintActionHandlers(false)
  const v2Handler = useV2MintActionHandlers(false)
  const charmHandler = useMintCharmActionHandlers()

  const {
    onFieldAInput,
    onFieldBInput,
    onSetSingleSided,
    onSetSingleAmount,
    onSetSingleSidedCurrency,
    onSetZapSwapSlippage,
  } = useMemo(() => {
    switch (poolType) {
      case 'V2':
        return {
          ...v2Handler,
          onSetSingleSided: undefined,
          onSetSingleAmount: undefined,
          onSetSingleSidedCurrency: undefined,
          onSetZapSwapSlippage: undefined,
          onSetSingleSidedStakingSlippageBPS: undefined,
        }
      case 'Charm':
        return {
          onFieldAInput: charmHandler.onFieldAInput,
          onFieldBInput: charmHandler.onFieldBInput,
          onSetSingleSided: islandHandler.onSetSingleSided,
          onSetSingleAmount: islandHandler.onSetSingleAmount,
          onSetSingleSidedCurrency: islandHandler.onSetSingleSidedCurrency,
          onSetZapSwapSlippage: islandHandler.onSetZapSwapSlippage,
          onSetSingleSidedStakingSlippageBPS: islandHandler.onSetSingleSidedStakingSlippageBPS,
        }
      default:
        return islandHandler
    }
  }, [poolType, islandHandler, v2Handler, charmHandler])

  const isValid = !error

  // Modal and transaction state
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [editValue, setEditValue] = useState<string>(islandMintState.zapSwapSlippage.toString())

  const [txHash, setTxHash] = useState<string>('')
  const deadline = useTransactionDeadline()

  // Formatted amounts
  const formattedAmounts = useMemo(() => {
    return {
      [independentField]: typedValue,
      [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
    }
  }, [independentField, typedValue, dependentField, parsedAmounts])

  // Clear simulation error when user changes inputs
  useEffect(() => {
    setSimulationError('')
  }, [
    islandMintState.singleSidedAmount,
    typedValue,
    islandMintState.zapSwapSlippage,
    islandMintState.singleSided,
    singleSidedCurrency,
  ])

  // Max amounts
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  const addTransaction = useTransactionAdder()

  const poolCurrency = useToken(poolAddress)

  const effectiveStakingType = useMemo(() => {
    if (stakingType === 'rewardVault' && useAutoCompound) {
      return 'bault'
    }
    return stakingType
  }, [stakingType, useAutoCompound])

  const estimatedOutputIslandTokens = useMemo(() => {
    // For single-sided deposits, use kxRouterMintInfo output for both Island and Charm pools
    if (
      islandMintState.singleSided &&
      kxRouterMintInfo?.outputAmount &&
      !useAutoCompound &&
      effectiveStakingType !== 'bault' &&
      (poolType === 'Island' || poolType === 'Charm')
    ) {
      return kxRouterMintInfo.outputAmount
    }

    if (poolType === 'V2') {
      return liquidityMinted
    }

    if (poolType === 'Charm') {
      return liquidityMinted
    }

    // For Island type, amountShares is now properly typed as CurrencyAmount<Token>
    return amountShares
  }, [
    poolType,
    liquidityMinted,
    amountShares,
    islandMintState.singleSided,
    kxRouterMintInfo,
    useAutoCompound,
    effectiveStakingType,
  ])

  const minReceived = useMemo(() => {
    if (poolType === 'V2') {
      return liquidityMinted?.multiply(99).divide(100)
    }

    // For single-sided deposits on Charm or Island, use kxRouterMintInfo outputAmountMin
    if (
      islandMintState.singleSided &&
      kxRouterMintInfo?.outputAmountMin &&
      !useAutoCompound &&
      effectiveStakingType !== 'bault' &&
      (poolType === 'Island' || poolType === 'Charm')
    ) {
      return kxRouterMintInfo.outputAmountMin
    }

    if (poolType === 'Charm') {
      return amountSharesMin
    }

    // For Island type, amountSharesMin is now properly typed as CurrencyAmount<Token>
    return amountSharesMin
  }, [
    poolType,
    liquidityMinted,
    amountSharesMin,
    islandMintState.singleSided,
    kxRouterMintInfo,
    useAutoCompound,
    effectiveStakingType,
  ])

  const [stakeAmount, resetStakeAmount] = useInputToken(estimatedOutputIslandTokens?.currency as Token)

  const {
    onStake: onFarmStake,
    attemptingTxnStake: attemptingTxnFarmStake,
    pendingTextStake: pendingTextFarmStake,
    txHashStake: txHashFarmStake,
    clear: clearFarmStake,
  } = useOnStake(stakeAmount, kodiakFarmAddress)

  const {
    onStake: onVaultStake,
    attemptingTxnStake: attemptingTxnVaultStake,
    pendingTextStake: pendingTextVaultStake,
    txHashStake: txHashVaultStake,
    clear: clearVaultStake,
  } = useOnVaultStake(stakeAmount, rewardVault)

  const shouldFetchShares = effectiveStakingType === 'bault' && !!baultAddress && !islandMintState.singleSided
  const {
    expectedShares: expectedSharesFromPreview,
    isLoading: isLoadingBaultSharesFromPreview,
    vaultName,
    vaultSymbol,
  } = usePreviewBaultShares(
    useAutoCompound ? baultAddress : undefined,
    poolCurrency
      ? CurrencyAmount.fromRawAmount(poolCurrency, estimatedOutputIslandTokens?.quotient.toString() ?? '0')
      : undefined,
    shouldFetchShares
  )

  const expectedShares = useMemo(() => {
    if (useAutoCompound && !!islandMintState.singleSided && !!baultAddress) {
      if (kxRouterMintInfo?.outputAmount) {
        return kxRouterMintInfo.outputAmount
      }
      return undefined
    }
    return expectedSharesFromPreview
  }, [
    kxRouterMintInfo?.outputAmount,
    expectedSharesFromPreview,
    islandMintState.singleSided,
    useAutoCompound,
    baultAddress,
  ])

  const {
    onStake: onBaultDeposit,
    attemptingTxnStake: attemptingTxnBaultDeposit,
    pendingTextStake: pendingTextBaultDeposit,
    txHashStake: txHashBaultDeposit,
    clear: clearBaultDeposit,
  } = useOnBaultDeposit(
    stakeAmount,
    routerAddress || v2RouterAddress || islandZapRouterAddress || undefined,
    useAutoCompound ? baultAddress : undefined,
    account
  )

  const clear = useCallback(() => {
    if (stakingType === 'kodiakFarm') {
      clearFarmStake()
    } else if (stakingType === 'rewardVault') {
      clearVaultStake()
    } else {
      clearBaultDeposit()
    }
  }, [stakingType, clearFarmStake, clearVaultStake, clearBaultDeposit])

  const handleDismissConfirmation = useCallback(() => {
    if (txHash) {
      onFieldAInput('')
    }
    setShowConfirm(false)
    setTxHash('')
    setSimulationError('')
    clear()
  }, [onFieldAInput, txHash, clear])

  const handleOptionChange = useCallback((value: any) => {
    userInteractedRef.current = true
    setDepositOption(value)
  }, [])

  const handleDoubleSidedCurrencySelect = useCallback(
    (field: Field) => (currency: Currency) => {
      setDoubleSidedCurrencies((prev) => ({
        ...prev,
        [field]: currency.isNative ? nativeCurrency : currency,
      }))
    },
    [nativeCurrency]
  )

  const getAmountString = (amount: any): string => {
    if (!amount) return '0'
    if (amount.quotient) {
      return amount.quotient.toString()
    }
    return amount.toString()
  }

  const getSignificant = (val: any, digits = 6) =>
    val && typeof val.toSignificant === 'function' ? val.toSignificant(digits) : val?.toString() || '0'

  function isTokenAmount(val: any): val is CurrencyAmount<Token> {
    return val && typeof val.toSignificant === 'function' && val.currency && val.currency.address
  }

  async function onAdd() {
    if (!chainId || !wagmiWalletClient || !account || !strategy) {
      console.warn('Missing required parameters for add liquidity')
      return
    }

    const { [Field.CURRENCY_A]: parsedAmountA, [Field.CURRENCY_B]: parsedAmountB } = parsedAmounts
    if (
      !parsedAmountA ||
      !parsedAmountB ||
      !effectiveCurrencies?.CURRENCY_A ||
      !effectiveCurrencies?.CURRENCY_B ||
      !deadline ||
      !wagmiPublicClient
    )
      return

    setAttemptingTxn(true)
    let responseHash: Hash | undefined

    try {
      const typedCurrencies: { [field: string]: Currency } = {}
      if (effectiveCurrencies[Field.CURRENCY_A])
        typedCurrencies[Field.CURRENCY_A] = effectiveCurrencies[Field.CURRENCY_A]
      if (effectiveCurrencies[Field.CURRENCY_B])
        typedCurrencies[Field.CURRENCY_B] = effectiveCurrencies[Field.CURRENCY_B]

      const params: DoubleSidedParams = {
        poolAddress,
        parsedAmountA,
        parsedAmountB,
        amount0Min: getAmountString(amount0Min),
        amount1Min: getAmountString(amount1Min),
        amountSharesMin: getAmountString(amountSharesMin),
        account,
        currencyA: effectiveCurrencies.CURRENCY_A,
        currencyB: effectiveCurrencies.CURRENCY_B,
        currencies: typedCurrencies,
        useAutoCompound: depositOption === 'depositOnly' && useAutoCompound && !!baultAddress,
        baultAddress,
        expectedShares,
        deadline,
      }

      responseHash = (await strategy.addLiquidityDoubleSided(params)) as Hash

      if (responseHash) {
        setTxHash(responseHash)

        if (params.useAutoCompound && !!baultAddress) {
          addTransaction({ hash: responseHash } as any, {
            type: TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT,
            baultAddress,
            tokenASymbol: effectiveCurrencies[Field.CURRENCY_A]?.symbol,
            tokenBSymbol: effectiveCurrencies[Field.CURRENCY_B]?.symbol,
            tokenAAmount: parsedAmountA.toExact(),
            tokenBAmount: parsedAmountB.toExact(),
            amount: formatSwapAmount(expectedShares) || '',
          })
        } else {
          const txType =
            poolType === 'V2'
              ? TransactionType.ADD_LIQUIDITY_V2_DEPOSIT_PAGE
              : poolType === 'Charm'
              ? TransactionType.ADD_LIQUIDITY_ISLAND
              : TransactionType.ADD_LIQUIDITY_ISLAND

          addTransaction({ hash: responseHash } as any, {
            type: txType,
            poolId: poolAddress,
            islandId: poolAddress,
            tokenASymbol: effectiveCurrencies[Field.CURRENCY_A]?.symbol,
            tokenBSymbol: effectiveCurrencies[Field.CURRENCY_B]?.symbol,
            tokenAAmount: parsedAmountA.toExact(),
            tokenBAmount: parsedAmountB.toExact(),
            feeAmount: feeAmount?.toString() || '',
            expectedAmountBaseRaw: formatSwapAmount(amountSharesMin),
          })
        }

        await waitForTransactionReceipt({ hash: responseHash })
      }
    } catch (error: any) {
      console.error('Failed to add liquidity:', error)
      if (error?.code !== 4001) {
        console.error('Transaction failed:', error)
      }
    } finally {
      setAttemptingTxn(false)
    }
  }

  const onAddSingle = useCallback(async () => {
    if (!strategy || !account || !wagmiWalletClient || !singleSidedCurrency || !wagmiPublicClient) return
    // Always try KX Router first for single-sided deposits
    if (kxRouterMintInfo && kxRouterMintInfo.kxRouterQuote) {
      setAttemptingTxn(true)
      let responseHash: Hash | undefined

      try {
        const params: SingleSidedParams & { kxRouterQuote?: any } = {
          poolAddress,
          amount: kxRouterMintInfo.singleSidedCurrencyAmount?.quotient.toString() || '0',
          slippage: kxRouterMintInfo.singleSidedStakingSlippageBPS,
          swapData: {
            amountIn: '0',
            minAmountOut: '0',
            zeroForOne: false,
            routeData: null,
          },
          account,
          currencyA: selectedCurrencyA,
          currencyB: selectedCurrencyB,
          currencies: effectiveCurrencies as { [field: string]: Currency },
          singleSidedCurrency,
          singleSidedCurrencyAddress: singleSidedCurrency.isToken
            ? singleSidedCurrency.address
            : singleSidedCurrency.wrapped.address,
          pool,
          useAutoCompound: depositOption === 'depositOnly' && useAutoCompound && !!baultAddress,
          baultAddress,
          expectedShares,
          kxRouterQuote: kxRouterMintInfo.kxRouterQuote,
        }

        responseHash = (await strategy.addLiquiditySingleSided(params)) as Hash

        if (responseHash) {
          setTxHash(responseHash)

          // Add transaction based on output type
          if (params.useAutoCompound && !!baultAddress) {
            addTransaction({ hash: responseHash } as any, {
              type: TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT_SINGLE_ZAP,
              baultAddress: baultAddress ?? '',
              inputToken: singleSidedCurrency.isToken ? singleSidedCurrency.address : 'native',
              inputTokenSymbol: singleSidedCurrency.symbol || '',
              inputAmount: formatSwapAmount(kxRouterMintInfo.singleSidedCurrencyAmount) || '0',
              outputAmount: formatSwapAmount(kxRouterMintInfo.outputAmount) || '',
              tokenASymbol: currencies[Field.CURRENCY_A]?.symbol ?? '',
              tokenBSymbol: currencies[Field.CURRENCY_B]?.symbol ?? '',
              provider: kxRouterMintInfo.provider || 'enso',
              isNative: false,
            })
          } else {
            addTransaction({ hash: responseHash } as any, {
              type: TransactionType.ADD_LIQUIDITY_ISLAND_SINGLE_ZAP,
              islandId: poolAddress,
              inputToken: singleSidedCurrency.isToken ? singleSidedCurrency.address : 'native',
              inputTokenSymbol: singleSidedCurrency.symbol || '',
              inputAmount: formatSwapAmount(kxRouterMintInfo.singleSidedCurrencyAmount) || '0',
              outputAmount: formatSwapAmount(kxRouterMintInfo.outputAmount) || '0',
              tokenASymbol: currencies[Field.CURRENCY_A]?.symbol,
              tokenBSymbol: currencies[Field.CURRENCY_B]?.symbol,
              provider: kxRouterMintInfo.provider || 'enso',
              isNative: false,
            })
          }

          await waitForTransactionReceipt({ hash: responseHash })
          try {
            await clearQuoteCache()
          } catch (cacheError) {
            console.warn('Failed to clear quote cache after single-sided zap:', cacheError)
          }
        }
        setAttemptingTxn(false)
        return
      } catch (error: any) {
        console.error('KX Router single-sided deposit failed:', error)
        console.log('error.code', error.code)
        setAttemptingTxn(false)

        // Handle specific error cases and set simulation error
        if (error?.code === 4001) {
          // User rejected - close modal without showing error
          handleDismissConfirmation()
          return
        } else {
          // Generic error message for unknown errors
          const errorMessage = 'Zap Simulation failed: ' + error?.message || 'Use Double Sided'
          setSimulationError(errorMessage)
        }
        // Keep modal open to show error
      }
    }
  }, [
    strategy,
    account,
    wagmiWalletClient,
    singleSidedCurrency,
    pool,
    depositOption,
    useAutoCompound,
    baultAddress,
    expectedShares,
    poolAddress,
    selectedCurrencyA,
    selectedCurrencyB,
    currencies,
    addTransaction,
    kxRouterMintInfo,
    effectiveCurrencies,
    handleDismissConfirmation,
    clearQuoteCache,
    wagmiPublicClient,
    waitForTransactionReceipt,
  ])

  const onAddNative = useCallback(async () => {
    if (!strategy || !account || !wagmiWalletClient || !singleSidedCurrency || !wagmiPublicClient) return

    // Clear any previous errors
    setAttemptingTxn(true)
    let responseHash: Hash | undefined

    try {
      const params: NativeParams & { kxRouterQuote?: any } = {
        poolAddress,
        amount: kxRouterMintInfo.singleSidedCurrencyAmount?.quotient.toString() || '0',
        slippage: kxRouterMintInfo.singleSidedStakingSlippageBPS,
        swapData: {
          amountIn: '0',
          minAmountOut: '0',
          zeroForOne: selectedCurrencyA.isNative,
          routeData: null,
        },
        account,
        currencyA: selectedCurrencyA,
        currencyB: selectedCurrencyB,
        currencies: effectiveCurrencies as { [field: string]: Currency },
        singleSidedCurrency,
        useAutoCompound: depositOption === 'depositOnly' && useAutoCompound && !!baultAddress,
        baultAddress,
        expectedShares,
        kxRouterQuote: kxRouterMintInfo.kxRouterQuote,
      }

      responseHash = (await strategy.addLiquidityNative(params)) as Hash

      if (responseHash) {
        setTxHash(responseHash)

        // Add transaction based on output type
        if (params.useAutoCompound && !!baultAddress) {
          addTransaction({ hash: responseHash } as any, {
            type: TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT_SINGLE_ZAP,
            baultAddress,
            inputToken: 'native',
            inputTokenSymbol: singleSidedCurrency.symbol || '',
            inputAmount: formatSwapAmount(kxRouterMintInfo.singleSidedCurrencyAmount) || '0',
            outputAmount: formatSwapAmount(kxRouterMintInfo.outputAmount) || '',
            tokenASymbol: currencies[Field.CURRENCY_A]?.symbol,
            tokenBSymbol: currencies[Field.CURRENCY_B]?.symbol,
            provider: kxRouterMintInfo.provider || 'enso',
            isNative: true,
          })
        } else {
          addTransaction({ hash: responseHash } as any, {
            type: TransactionType.ADD_LIQUIDITY_ISLAND_SINGLE_ZAP,
            islandId: poolAddress,
            inputToken: 'native',
            inputTokenSymbol: singleSidedCurrency.symbol || '',
            inputAmount: formatSwapAmount(kxRouterMintInfo.singleSidedCurrencyAmount) || '0',
            outputAmount: formatSwapAmount(kxRouterMintInfo.outputAmount) || '0',
            tokenASymbol: currencies[Field.CURRENCY_A]?.symbol,
            tokenBSymbol: currencies[Field.CURRENCY_B]?.symbol,
            provider: kxRouterMintInfo.provider || 'enso',
            isNative: true,
          })
        }

        if (wagmiPublicClient && responseHash) {
          await waitForTransactionReceipt({ hash: responseHash })
          try {
            await clearQuoteCache()
          } catch (cacheError) {
            console.warn('Failed to clear quote cache after native single-sided zap:', cacheError)
          }
        }
      }
      setAttemptingTxn(false)
      return // Success - exit function
    } catch (error: any) {
      console.error('KX Router native token deposit failed:', error)
      setAttemptingTxn(false)

      // Handle specific error cases and set simulation error
      if (error?.code === 4001) {
        // User rejected - close modal without showing error
        handleDismissConfirmation()
        return
      } else {
        // Generic error message for unknown errors
        const errorMessage = `Zap Simulation failed: ${error?.message || 'Use Double Sided'}`
        setSimulationError(errorMessage)
      }
      // Keep modal open to show error
    }
  }, [
    strategy,
    account,
    wagmiWalletClient,
    singleSidedCurrency,
    selectedCurrencyA,
    selectedCurrencyB,
    depositOption,
    useAutoCompound,
    baultAddress,
    expectedShares,
    poolAddress,
    currencies,
    addTransaction,
    kxRouterMintInfo,
    effectiveCurrencies,
    handleDismissConfirmation,
    clearQuoteCache,
    wagmiPublicClient,
    waitForTransactionReceipt,
  ])

  const pendingText = (
    <>
      Supplying {formatSwapAmount(parsedAmounts[Field.CURRENCY_A])} {currencies[Field.CURRENCY_A]?.symbol} and{' '}
      {formatSwapAmount(parsedAmounts[Field.CURRENCY_B])} {currencies[Field.CURRENCY_B]?.symbol}
    </>
  )

  const pendingTextSingleSided = useMemo(() => {
    if (!singleSidedCurrency || !kxRouterMintInfo?.singleSidedCurrencyAmount) return null

    const baseText = `Zapping ${formatSwapAmount(kxRouterMintInfo.singleSidedCurrencyAmount)} ${
      singleSidedCurrency.symbol
    } into ${currencies[Field.CURRENCY_A]?.symbol}/${currencies[Field.CURRENCY_B]?.symbol} Island`

    if (depositOption === 'depositOnly' && useAutoCompound && !!baultAddress) {
      return <>{baseText.replace('Island', 'Bault')}</>
    }

    return <>{baseText}</>
  }, [singleSidedCurrency, kxRouterMintInfo, currencies, depositOption, useAutoCompound, baultAddress])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const routerAddressForApproval = useMemo(() => {
    return strategy?.routerAddress
  }, [strategy])

  const approveStep0 = useApproveStep(
    effectiveCurrencies?.CURRENCY_A && parsedAmounts[Field.CURRENCY_A]
      ? CurrencyAmount.fromRawAmount(effectiveCurrencies.CURRENCY_A, parsedAmounts[Field.CURRENCY_A].quotient)
      : undefined,
    routerAddressForApproval
  )
  const approveStep1 = useApproveStep(
    effectiveCurrencies?.CURRENCY_B && parsedAmounts[Field.CURRENCY_B]
      ? CurrencyAmount.fromRawAmount(effectiveCurrencies.CURRENCY_B, parsedAmounts[Field.CURRENCY_B].quotient)
      : undefined,
    routerAddressForApproval
  )

  const singleSidedCurrencyAmount = singleSidedCurrency
    ? CurrencyAmount.fromRawAmount(singleSidedCurrency, islandMintInfo.singleSidedParsedAmount || '0')
    : undefined

  const approveStepSingleSided = useApproveStep(singleSidedCurrencyAmount, routerAddressForApproval)

  const approvalAmount = useMemo(() => {
    if (useAutoCompound && kodiakFarmForBaultExists) {
      return expectedShares
    } else {
      return estimatedOutputIslandTokens
    }
  }, [useAutoCompound, kodiakFarmForBaultExists, expectedShares, estimatedOutputIslandTokens])

  const approveLp = useApproveStep(
    approvalAmount,
    stakingType === 'kodiakFarm' || (useAutoCompound && kodiakFarmForBaultExists)
      ? kodiakFarm?.id
      : stakingType === 'rewardVault'
      ? rewardVault
      : strategy?.routerAddress
  )

  const selectLockTimeStep = useSelectLockTimeStep(
    kodiakFarmAddress,
    useAutoCompound && kodiakFarmForBaultExists ? expectedShares?.toExact() : estimatedOutputIslandTokens?.toExact()
  )

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      onSetSingleSidedCurrency?.(currency.isNative ? 'native' : currency.address)
      onFieldAInput('')
    },
    [onSetSingleSidedCurrency, onFieldAInput]
  )

  const depositStepDoubleSided = useIslandDepositStep({
    noLiquidity: false,
    currencies,
    liquidityMinted: formatSwapAmount(estimatedOutputIslandTokens),
    parsedAmounts,
    price: undefined,
    amountSharesMin: formatSwapAmount(minReceived),
    onAdd,
    attemptingTxn,
    pendingText,
    hash: txHash,
    useAutoCompound: depositOption === 'depositOnly' && useAutoCompound && !!baultAddress,
    expectedShares: formatSwapAmount(expectedShares),
    vaultSymbol,
    isLoading: isLoadingBaultSharesFromPreview,
    autoConfirm: true,
  })

  const depositStepSingleSided = useIslandDepositStep({
    noLiquidity: false,
    currencies,
    liquidityMinted: formatSwapAmount(estimatedOutputIslandTokens),
    parsedAmounts,
    price: undefined,
    amountSharesMin: formatSwapAmount(minReceived),
    useAutoCompound: depositOption === 'depositOnly' && useAutoCompound && !!baultAddress,
    expectedShares: formatSwapAmount(expectedShares),
    vaultSymbol,
    onAdd: onAddSingle,
    attemptingTxn,
    pendingText: pendingTextSingleSided,
    hash: txHash,
    autoConfirm: true,
    isSingleSided: true,
    singleSidedCurrency: singleSidedCurrency || undefined,
    singleSidedAmount: kxRouterMintInfo?.singleSidedCurrencyAmount,
  })
  depositStepSingleSided.title = 'Confirm Zap'

  const depositStepNative = useIslandDepositStep({
    noLiquidity: false,
    currencies,
    liquidityMinted: formatSwapAmount(estimatedOutputIslandTokens),
    parsedAmounts,
    price: undefined,
    amountSharesMin: getSignificant(minReceived),
    useAutoCompound: depositOption === 'depositOnly' && useAutoCompound && !!baultAddress,
    expectedShares: formatSwapAmount(expectedShares),
    vaultSymbol,
    onAdd: onAddNative,
    attemptingTxn,
    pendingText: pendingTextSingleSided,
    hash: txHash,
    autoConfirm: true,
    isSingleSided: true,
    singleSidedCurrency: singleSidedCurrency || undefined,
    singleSidedAmount: kxRouterMintInfo?.singleSidedCurrencyAmount,
  })
  depositStepNative.title = 'Confirm Deposit'

  const farmStakeStep = useStepWithNext('StakeStep', StakeStep, {
    attemptingTxn: attemptingTxnFarmStake,
    pendingText: pendingTextFarmStake,
    hash: txHashFarmStake,
    amount: stakeAmount,
    onStake: onFarmStake,
    transactionWaiting: true,
  })

  const rewardVaultStakeStep = useStepWithNext('RewardVaultStakeStep', RewardVaultStakeStep, {
    amount: stakeAmount,
    onStake: onVaultStake,
    hash: txHashVaultStake,
    attemptingTxn: attemptingTxnVaultStake,
    rewardVault,
    apr: poolApiResponse?.farm?.apr,
    rewardVaultRewardSymbol,
    pendingText: pendingTextVaultStake,
    transactionWaiting: true,
  })

  const baultDepositStep = useStepWithNext('BaultDepositStep', BaultDepositStep, {
    amount: expectedShares,
    attemptingTxn: attemptingTxnBaultDeposit,
    hash: txHashBaultDeposit,
    onStake: onBaultDeposit,
    baultAddress,
    pendingText: pendingTextBaultDeposit,
    transactionWaiting: true,
  })

  const stakeStep = useMemo(() => {
    if (stakingType === 'rewardVault' && useAutoCompound && !!baultAddress) {
      return baultDepositStep
    }
    return stakingType === 'kodiakFarm' ? farmStakeStep : rewardVaultStakeStep
  }, [stakingType, useAutoCompound, baultAddress, farmStakeStep, rewardVaultStakeStep, baultDepositStep])

  // Confirmation steps
  const confirmationSteps = [
    ...((poolType === 'Island' || poolType === 'Charm') && singleSidedCurrency?.isNative
      ? []
      : (poolType === 'Island' || poolType === 'Charm') && islandMintState.singleSided
      ? [approveStepSingleSided]
      : [approveStep0, approveStep1]),

    (poolType === 'Island' || poolType === 'Charm') && depositStepSingleSided && singleSidedCurrency?.isNative
      ? depositStepNative
      : (poolType === 'Island' || poolType === 'Charm') && islandMintState.singleSided
      ? depositStepSingleSided
      : depositStepDoubleSided,

    ...(depositOption === 'withStaking' && (!!kodiakFarmAddress || !!rewardVault)
      ? [approveLp, stakingType === 'kodiakFarm' ? selectLockTimeStep : undefined, stakeStep]
      : []),
  ].filter(Boolean)

  // Price impact calculation for KX Router
  const priceImpactPercent = useMemo(() => {
    return kxRouterMintInfo?.sanitizedPriceImpact
  }, [kxRouterMintInfo])

  const shouldShowPriceImpactWarning = islandMintState.singleSided && warningSeverity(priceImpactPercent) >= 4

  const tokenAddresses = useMemo(() => {
    const addresses = [selectedCurrencyA.wrapped.address, selectedCurrencyB.wrapped.address]

    // Add single-sided currency address if it's different from pool currencies and in single-sided mode
    if (islandMintState.singleSided && singleSidedCurrency) {
      const singleSidedAddress = singleSidedCurrency.wrapped.address
      if (!addresses.includes(singleSidedAddress)) {
        addresses.push(singleSidedAddress)
      }
    }

    return addresses
  }, [
    selectedCurrencyA.wrapped.address,
    selectedCurrencyB.wrapped.address,
    islandMintState.singleSided,
    singleSidedCurrency,
  ])

  const { prices: tokenPrices } = useGetTokenPrices(tokenAddresses)

  const [currencyAPrice, currencyBPrice] = tokenPrices || []

  const getSingleSidedCurrencyPrice = useCallback(() => {
    if (!singleSidedCurrency || !tokenAddresses || !tokenPrices) return null

    const singleSidedAddress = singleSidedCurrency.wrapped.address.toLowerCase()

    // Find the index of the single-sided currency in the tokenAddresses array
    const addressIndex = tokenAddresses.findIndex((address) => address.toLowerCase() === singleSidedAddress)

    // Return the corresponding price from the tokenPrices array
    if (addressIndex !== -1 && tokenPrices[addressIndex] !== null && tokenPrices[addressIndex] !== undefined) {
      return tokenPrices[addressIndex]
    }

    // Return null if currency not found or price not available
    return null
  }, [singleSidedCurrency, tokenAddresses, tokenPrices])

  const singleSidedUSDValue = useUSDValue(islandMintState.singleSidedAmount, getSingleSidedCurrencyPrice())
  const currencyAUSDValue = useUSDValue(formattedAmounts[Field.CURRENCY_A], currencyAPrice)
  const currencyBUSDValue = useUSDValue(formattedAmounts[Field.CURRENCY_B], currencyBPrice)

  const buttonText = useMemo(() => {
    if (islandMintState.singleSided) {
      // Always use KX Router for single-sided deposits
      if (singleSidedCurrencyAmount?.equalTo('0')) {
        return <>Enter an amount</>
      }
      if (kxRouterMintInfo) {
        if (kxRouterMintInfo.isLoading) {
          return <>Preparing zap</>
        }
        if (kxRouterMintInfo.error) {
          return kxRouterMintInfo.error
        }
        return <>Confirm Zap</>
      }
      if (error) {
        return error
      }
      return <>Confirm Zap</>
    }
    if (error) {
      return error
    }
    return <>Supply</>
  }, [islandMintState.singleSided, error, kxRouterMintInfo, singleSidedCurrencyAmount])

  const awaitingSingleSidedInput = useMemo(() => {
    return (
      islandMintState.singleSided &&
      (!kxRouterMintInfo?.singleSidedCurrencyAmount || kxRouterMintInfo?.singleSidedCurrencyAmount.equalTo('0'))
    )
  }, [islandMintState.singleSided, kxRouterMintInfo?.singleSidedCurrencyAmount])

  const zapQuoteRefetch = kxRouterMintInfo.refetch

  const zapRefreshDisabled = useMemo(() => {
    if (!zapQuoteRefetch) return true
    if (awaitingSingleSidedInput) return true
    if (isRefreshingZapQuote) return true
    if (kxRouterMintInfo?.isLoading) return true
    return false
  }, [awaitingSingleSidedInput, isRefreshingZapQuote, kxRouterMintInfo?.isLoading, zapQuoteRefetch])

  const handleRefreshZapQuote = useCallback(async () => {
    if (!zapQuoteRefetch || isRefreshingZapQuote) return
    setIsRefreshingZapQuote(true)
    try {
      await zapQuoteRefetch()
    } catch (error) {
      console.error('Failed to refresh zap quote:', error)
    } finally {
      setTimeout(() => setIsRefreshingZapQuote(false), 500)
    }
  }, [isRefreshingZapQuote, zapQuoteRefetch])

  const isDepositDisabled = useMemo(() => {
    if (islandMintState.singleSided) {
      if (
        awaitingSingleSidedInput ||
        kxRouterMintInfo?.isLoading ||
        kxRouterMintInfo?.isError ||
        kxRouterMintInfo?.error ||
        kxRouterMintInfo?.hasThresholdViolation
      ) {
        return true
      }
    }
    if (!islandMintState.singleSided && (!parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B])) {
      return true
    }
    if (
      !isValid &&
      !islandMintState.singleSided &&
      !!parsedAmounts[Field.CURRENCY_A] &&
      !!parsedAmounts[Field.CURRENCY_B]
    ) {
      return true
    }
    return false
  }, [islandMintState.singleSided, awaitingSingleSidedInput, kxRouterMintInfo, isValid, parsedAmounts])

  const isDepositError = useMemo(() => {
    if (islandMintState.singleSided) {
      if (!!kxRouterMintInfo?.isError || !!kxRouterMintInfo?.error || !!kxRouterMintInfo?.hasThresholdViolation) {
        return true
      }
    }
    if (!isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]) {
      return true
    }
    return false
  }, [islandMintState.singleSided, isValid, parsedAmounts, kxRouterMintInfo])

  const showAutoCompoundOption = useMemo(() => {
    return !!baultAddress && stakingType === 'rewardVault' && !isRewardVaultStakingDisabled
  }, [baultAddress, stakingType, isRewardVaultStakingDisabled])

  const toggleAutoCompound = useCallback(() => {
    setAutoCompoundSelected((prev) => {
      const newAutoCompoundState = !prev
      if (newAutoCompoundState) {
        if (kodiakFarmForBaultExists) {
          setDepositOption('withStaking')
        } else {
          setDepositOption('depositOnly')
        }
      } else {
        setDepositOption('withStaking')
      }
      return newAutoCompoundState
    })
  }, [kodiakFarmForBaultExists])

  const autoCompoundHelperText = useMemo(() => {
    if (useAutoCompound) {
      if (kodiakFarmForBaultExists) {
        return depositOption === 'withStaking'
          ? 'Auto-compounding automatically reinvests your rewards. Your bault tokens will also be staked in a Kodiak Farm to earn additional rewards.'
          : 'Auto-compounding automatically reinvests your rewards. You can also choose to stake these bault tokens in a Kodiak Farm to earn additional rewards.'
      } else {
        return 'Auto-compounding automatically reinvests your rewards into the pool, maximizing your returns over time.'
      }
    } else {
      if (depositOption === 'depositOnly') {
        return `Your LP tokens will be deposited to the ${
          poolType === 'Charm' ? 'Charm vault' : 'pool'
        } without auto-compounding or staking.`
      } else {
        return `When auto-compound is disabled, your LP tokens will be staked in the ${rewardVaultLabel}, earning rewards that can be manually claimed but will not automatically compound.`
      }
    }
  }, [useAutoCompound, kodiakFarmForBaultExists, depositOption, poolType, rewardVaultLabel])

  const hasValidInput = useMemo(() => {
    if (islandMintState.singleSided) {
      return !!(kxRouterMintInfo?.singleSidedCurrencyAmount && !kxRouterMintInfo.singleSidedCurrencyAmount.equalTo('0'))
    }
    return !!(parsedAmounts[Field.CURRENCY_A] && parsedAmounts[Field.CURRENCY_B])
  }, [islandMintState.singleSided, kxRouterMintInfo?.singleSidedCurrencyAmount, parsedAmounts])

  const canSelectNativeForCurrencyA =
    selectedCurrencyA?.wrapped?.address?.toLowerCase() === wberaToken.address.toLowerCase() && poolType !== 'Charm'
  const canSelectNativeForCurrencyB =
    selectedCurrencyB?.wrapped?.address?.toLowerCase() === wberaToken.address.toLowerCase() && poolType !== 'Charm'

  return (
    <>
      <StepLineModal
        isOpen={showConfirm}
        onDismiss={handleDismissConfirmation}
        steps={confirmationSteps}
        header={
          <ThemedText.DeprecatedMediumHeader>
            Deposit Liquidity to {poolType === 'Charm' ? 'Charm Vault' : poolType === 'V2' ? 'V2 Pool' : 'Island'}
          </ThemedText.DeprecatedMediumHeader>
        }
        onFinish={handleDismissConfirmation}
        onStart={resetStakeAmount}
        simulationError={simulationError}
      />
      {poolType !== 'V2' && (
        <S.Switch>
          <S.Option
            onClick={() => onSetSingleSided?.(false, '')}
            active={!islandMintState.singleSided && !poolSingleSided}
            tabIndex={-1}
          >
            <>Double-sided</>
          </S.Option>
          <S.Option
            onClick={() => {
              onSetSingleSided?.(
                true,
                currencies[Field.CURRENCY_A]?.isNative ? 'native' : currencies[Field.CURRENCY_A]?.address ?? ''
              )
              setDepositOption('depositOnly')
            }}
            active={islandMintState.singleSided || !!poolSingleSided}
          >
            <>Single-sided</>
          </S.Option>
        </S.Switch>
      )}

      {islandMintState.singleSided ? (
        <CurrencyInputPanel
          value={islandMintState.singleSidedAmount}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onUserInput={onSetSingleAmount ?? (() => {})}
          onMax={() => onSetSingleAmount?.(islandMintInfo.singleSidedCurrencyUserBalance?.toExact() ?? '')}
          loading={false}
          showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
          currency={singleSidedCurrency ?? null}
          onCurrencySelect={handleCurrencySelect}
          id="add-liquidity-input-tokena"
          allowNativeToken={currencyA?.isNative || currencyB?.isNative || false}
          preferredCurrencies={[currencyA, currencyB].filter(Boolean) as Currency[]}
          showCommonBases
          apiUSDValue={singleSidedUSDValue}
        />
      ) : (
        <>
          <CurrencyInputPanel
            value={formattedAmounts[Field.CURRENCY_A]}
            onUserInput={onFieldAInput}
            onMax={() => {
              onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
            }}
            loading={dependentField === Field.CURRENCY_A && dependentAmountFetching}
            showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
            currency={effectiveCurrencies.CURRENCY_A ?? null}
            id="add-liquidity-input-tokena"
            apiUSDValue={currencyAUSDValue}
            onCurrencySelect={
              canSelectNativeForCurrencyA ? handleDoubleSidedCurrencySelect(Field.CURRENCY_A) : undefined
            }
            allowedCurrencies={canSelectNativeForCurrencyA ? [wberaToken.address] : undefined}
            showCategories={false}
            compactSearchModal
          />
          <CurrencyInputPanel
            value={formattedAmounts[Field.CURRENCY_B]}
            onUserInput={onFieldBInput}
            onMax={() => {
              onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
            }}
            loading={dependentField === Field.CURRENCY_B && dependentAmountFetching}
            showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
            currency={effectiveCurrencies.CURRENCY_B ?? null}
            id="add-liquidity-input-tokenb"
            apiUSDValue={currencyBUSDValue}
            onCurrencySelect={
              canSelectNativeForCurrencyB ? handleDoubleSidedCurrencySelect(Field.CURRENCY_B) : undefined
            }
            allowedCurrencies={canSelectNativeForCurrencyB ? [wberaToken.address] : undefined}
            showCategories={false}
            compactSearchModal
          />
        </>
      )}
      {!islandMintState.singleSided && (
        <S.InnerIslandCardFlex>
          <S.CurrencyFlexBetween>
            <S.TransactionInfoText>Est. Received</S.TransactionInfoText>
            <S.TransactionInfoText>
              {formatSwapAmount(estimatedOutputIslandTokens) || '0'}{' '}
              {poolCurrency?.symbol?.replace('RAKIS', 'KODIAK') || 'LP'} (
              <FiatValue
                currencyAmount={isTokenAmount(estimatedOutputIslandTokens) ? estimatedOutputIslandTokens : null}
              />
              )
            </S.TransactionInfoText>
          </S.CurrencyFlexBetween>
          <S.CurrencyFlexBetween>
            <S.TransactionInfoText>Min. Received</S.TransactionInfoText>
            <S.TransactionInfoText>
              {formatSwapAmount(minReceived) || '0'} {poolCurrency?.symbol?.replace('RAKIS', 'KODIAK') || 'LP'} (
              <FiatValue currencyAmount={isTokenAmount(minReceived) ? minReceived : null} />)
            </S.TransactionInfoText>
          </S.CurrencyFlexBetween>
        </S.InnerIslandCardFlex>
      )}
      {islandMintState.singleSided && (
        // KX Router UI for single-sided deposits
        <>
          <S.CurrencyFlexBetween>
            <ThemedText.DeprecatedWhite fontSize={14} fontWeight={500}>
              Zap Details
            </ThemedText.DeprecatedWhite>
            <RefreshButton
              type="button"
              onClick={handleRefreshZapQuote}
              disabled={zapRefreshDisabled}
              isRefreshing={isRefreshingZapQuote}
              title="Refresh zap quote"
            >
              <RefreshCw size={16} />
            </RefreshButton>
          </S.CurrencyFlexBetween>
          <S.InnerIslandCardFlex>
            <S.CurrencyFlexBetween>
              <S.TransactionInfoText>Token Out Amount</S.TransactionInfoText>
              <S.TransactionInfoText>
                {kxRouterMintInfo.isLoading
                  ? 'Loading...'
                  : kxRouterMintInfo.isError
                  ? '-'
                  : `${formatSwapAmount(kxRouterMintInfo.outputAmount) || '0'} Pool Tokens`}
              </S.TransactionInfoText>
            </S.CurrencyFlexBetween>
            <S.CurrencyFlexBetween>
              <S.TransactionInfoText>Min. Received</S.TransactionInfoText>
              <S.TransactionInfoText>
                {kxRouterMintInfo.isLoading
                  ? 'Loading...'
                  : kxRouterMintInfo.isError
                  ? '-'
                  : `${formatSwapAmount(kxRouterMintInfo.outputAmountMin) || '0'} Pool Tokens`}
              </S.TransactionInfoText>
            </S.CurrencyFlexBetween>
            <S.CurrencyFlexBetween>
              <S.TransactionInfoText>Routing Via</S.TransactionInfoText>
              <S.TransactionInfoText>
                {kxRouterMintInfo.isLoading
                  ? 'Loading...'
                  : kxRouterMintInfo.isError
                  ? '-'
                  : `kX (${kxRouterMintInfo.provider || ''})`}
              </S.TransactionInfoText>
            </S.CurrencyFlexBetween>
            <S.CurrencyFlexBetween>
              <S.TransactionInfoText>Price Impact</S.TransactionInfoText>
              <ErrorText
                fontWeight={400}
                fontSize={12}
                severity={
                  kxRouterMintInfo.isLoading || kxRouterMintInfo.error || kxRouterMintInfo.isError
                    ? 0
                    : warningSeverity(priceImpactPercent ?? null)
                }
              >
                {kxRouterMintInfo.isLoading
                  ? 'Loading...'
                  : kxRouterMintInfo.isError
                  ? '-'
                  : priceImpactPercent
                  ? `${priceImpactPercent.toFixed(2)}%`
                  : priceImpactPercent === null
                  ? 'Unknown'
                  : '-'}
              </ErrorText>
            </S.CurrencyFlexBetween>
            <S.CurrencyFlexBetween>
              <S.TransactionInfoText>
                Slippage Tolerance
                <span style={{ fontSize: '10px', opacity: 0.6, marginLeft: '4px' }}>
                  ({MIN_ZAP_SLIPPAGE_PERCENTAGE}% - {MAX_ZAP_SLIPPAGE_PERCENTAGE}%)
                </span>
              </S.TransactionInfoText>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {isEditing ? (
                  <input
                    type="number"
                    max={MAX_ZAP_SLIPPAGE_PERCENTAGE}
                    min={MIN_ZAP_SLIPPAGE_PERCENTAGE}
                    step={0.01}
                    value={editValue}
                    onChange={(e) => {
                      const value = e.target.value
                      const numValue = parseFloat(value)

                      // Allow typing but enforce bounds on blur
                      if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 100)) {
                        setEditValue(value)
                      }
                    }}
                    onBlur={() => {
                      setIsEditing(false)
                      // Enforce bounds when updating state
                      let finalValue = editValue
                      const numValue = parseFloat(editValue)

                      if (editValue === '' || isNaN(numValue)) {
                        finalValue = islandMintState.zapSwapSlippage
                      } else if (numValue < MIN_ZAP_SLIPPAGE_PERCENTAGE) {
                        finalValue = MIN_ZAP_SLIPPAGE_PERCENTAGE.toString()
                      } else if (numValue > MAX_ZAP_SLIPPAGE_PERCENTAGE) {
                        finalValue = MAX_ZAP_SLIPPAGE_PERCENTAGE.toString()
                      }
                      setEditValue(finalValue)
                      onSetZapSwapSlippage?.(finalValue)
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '2px 4px',
                      color: 'inherit',
                      width: '60px',
                    }}
                    autoFocus
                  />
                ) : (
                  <S.TransactionInfoText>
                    {islandMintState.zapSwapSlippage}%
                    <button
                      onClick={() => setIsEditing(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0 4px',
                        cursor: 'pointer',
                        color: 'white',
                        verticalAlign: 'middle',
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-edit-3"
                      >
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                      </svg>
                    </button>
                  </S.TransactionInfoText>
                )}
              </div>
            </S.CurrencyFlexBetween>
          </S.InnerIslandCardFlex>
          {shouldShowPriceImpactWarning && <PriceImpactWarning priceImpact={priceImpactPercent} />}
        </>
      )}

      <AutoColumn gap="md">
        {!islandMintState.singleSided &&
          !isRewardVaultStakingDisabled &&
          (stakingType === 'kodiakFarm' || stakingType === 'rewardVault') && (
            <Switch
              options={[
                { value: 'depositOnly', label: 'Deposit Only' },
                { value: 'withStaking', label: 'With Staking' },
              ]}
              activeValue={depositOption}
              setActiveValue={handleOptionChange}
              disabled={useAutoCompound && shouldDisableWithStaking}
            />
          )}
        {showAutoCompoundOption && (
          <AutoCompoundDets
            isActive={useAutoCompound}
            onToggle={toggleAutoCompound}
            currency={poolCurrency}
            baultAddress={baultAddress}
            expectedShares={
              islandMintState.singleSided && (kxRouterMintInfo.error || kxRouterMintInfo.isError)
                ? undefined
                : expectedShares
            }
            isLoading={
              hasValidInput &&
              ((!islandMintState.singleSided && isLoadingBaultSharesFromPreview) ||
                (!awaitingSingleSidedInput && kxRouterMintInfo.isLoading && !kxRouterMintInfo.isError))
            }
            vaultSymbol={vaultSymbol}
            helperText={autoCompoundHelperText}
            apy={data?.apy}
            rewardVaultRewardSymbol={rewardVaultRewardSymbol}
          />
        )}
      </AutoColumn>

      {addIsUnsupported ? (
        <ButtonPrimary disabled={true}>
          <ThemedText.DeprecatedMain mb="4px">
            <>Unsupported Asset</>
          </ThemedText.DeprecatedMain>
        </ButtonPrimary>
      ) : !account ? (
        <ButtonLight onClick={toggleWalletModal}>
          <>Connect Wallet</>
        </ButtonLight>
      ) : (
        <ButtonError
          onClick={() => {
            setSimulationError('')
            setShowConfirm(true)
          }}
          disabled={isDepositDisabled}
          error={isDepositError}
        >
          <ThemedText.DeprecatedWhite fontSize={20} fontWeight={500}>
            {buttonText}
          </ThemedText.DeprecatedWhite>
        </ButtonError>
      )}
    </>
  )
}

export default React.memo(DepositView, (prevProps, nextProps) => {
  const samePoolAddress = prevProps.islandId === nextProps.islandId
  const sameBaultAddress = prevProps.baultAddress === nextProps.baultAddress
  const sameFarmAddress = prevProps.farmAddress === nextProps.farmAddress
  const sameRewardVault = prevProps.rewardVault === nextProps.rewardVault
  const sameStakingType = prevProps.stakingType === nextProps.stakingType
  const samePoolType = prevProps.poolType === nextProps.poolType

  return samePoolAddress && sameBaultAddress && sameFarmAddress && sameRewardVault && sameStakingType && samePoolType
})
